import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { MaterialContext } from "../context/MaterialContext";
import Swatch from "../components/Swatch";
import LoadingDots from "../components/LoadingDots";
import LibraryResultBanner from "../components/LibraryResultBanner";
import { getGridColumnCount, remainingGridSpan } from "../utilities/gridColumns";

function Library() {
  const {
    materials,
    materialsLoaded,
    search,
    changeSearch,
    status,
    changeStatus,
    orderBy,
    isFilter,
    changeIsFilter,
    brandArticlesOnly,
    changeBrandArticlesOnly,
  } = useContext(MaterialContext);
  const gridRef = useRef(null);
  const [bannerSpan, setBannerSpan] = useState(1);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [search, status, orderBy, brandArticlesOnly]);

  const handleClearFilterSearch = () => {
    changeIsFilter(false);
    changeSearch("");
    changeBrandArticlesOnly(false);
  };

  const viewingNonCommercial = status !== "commercial";
  const viewedStatusLabel = status === "none" ? "all" : status;
  const showStatusBanner = viewingNonCommercial;
  const showSearchBanner = search && !isFilter && !viewingNonCommercial;
  const showFilterBanner = isFilter && !search && !viewingNonCommercial;
  const showBothBanner = isFilter && search && !viewingNonCommercial;
  const showResultBanner =
    showStatusBanner || showSearchBanner || showFilterBanner || showBothBanner;

  const handleViewCommercial = () => {
    changeStatus("commercial");
    if (!brandArticlesOnly) {
      changeIsFilter(false);
    }
  };

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid || !showResultBanner) {
      return undefined;
    }

    const updateSpan = () => {
      setBannerSpan(
        remainingGridSpan(materials.length, getGridColumnCount(grid)),
      );
    };

    updateSpan();

    const observer = new ResizeObserver(updateSpan);
    observer.observe(grid);
    window.addEventListener("resize", updateSpan);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSpan);
    };
  }, [materials.length, showResultBanner, materialsLoaded]);

  const bannerStyle = {
    gridColumn: `span ${Math.max(1, bannerSpan)}`,
  };

  return (
    <div className="content">
      {!materialsLoaded ? (
        <LoadingDots
          label="Loading Material Swatches..."
          containerClassName="libraryLoadingState"
          labelClassName="libraryLoadingText"
          dotsClassName="libraryLoadingDots"
        />
      ) : null}
      <div className="swatch-view" ref={gridRef}>
        {materialsLoaded &&
          materials?.map((material) => (
            <Swatch key={material.id} id={material.id} />
          ))}
        {showStatusBanner ? (
          <LibraryResultBanner
            count={materials.length}
            kicker={`${materials.length} results`}
            queryLabel={`Viewing ${viewedStatusLabel} materials`}
            actionLabel="View commercial materials"
            onAction={handleViewCommercial}
            style={bannerStyle}
          />
        ) : null}
        {showSearchBanner ? (
          <LibraryResultBanner
            count={materials.length}
            queryLabel={`"${search}"`}
            actionLabel="Clear Search"
            onAction={handleClearFilterSearch}
            style={bannerStyle}
          />
        ) : null}
        {showFilterBanner ? (
          <LibraryResultBanner
            count={materials.length}
            queryLabel="Filter"
            actionLabel="Clear Filter"
            onAction={handleClearFilterSearch}
            style={bannerStyle}
          />
        ) : null}
        {showBothBanner ? (
          <LibraryResultBanner
            count={materials.length}
            queryLabel="Filter & Search"
            actionLabel="Clear All"
            onAction={handleClearFilterSearch}
            style={bannerStyle}
          />
        ) : null}
      </div>
    </div>
  );
}

export default Library;
